/**
 * SeatDetailPanel.tsx
 * 좌석 상세 정보 사이드 패널
 * - 학생 기본 정보
 * - 주간 스케줄
 * - 오늘의 입출입 기록
 * - 최근 출석 요약
 */

import React, { useState, useEffect } from 'react';
import { XMarkIcon, UserCircleIcon, CalendarIcon, ClockIcon, ArrowRightOnRectangleIcon, AcademicCapIcon, ChevronDownIcon, ChevronUpIcon, BellAlertIcon } from '@heroicons/react/24/outline';
import {
  SeatDetailInfo,
  PatrolNoteCreate,
} from '../../repositories/SeatOperationRepository';
import StudentTodaySummary from './StudentTodaySummary';  // ⭐ B작업 Step 1 — 오늘 종합
import PatrolChecksStrip from './PatrolChecksStrip';
import StudentTimeBand from './StudentTimeBand';  // 시간 단일진실 — 오늘 하루 시간띠
import { apiClient } from '../../../../services/authApi';
import { getTodayKST } from '../../../../utils/dateUtils';

interface SeatDetailPanelProps {
  seatDetail: SeatDetailInfo | null;
  onClose: () => void;
  onCreateNote: (seatId: string, note: PatrolNoteCreate) => Promise<void>;
  onUnassignStudent: (seatId: string) => Promise<void>;
  onRefresh?: (seatId: string) => Promise<void>;
}

interface ScheduleInfo {
  day_of_week: number;
  day_name: string;
  expected_check_in: string;
  expected_check_out: string;
  is_active: boolean;
}

interface AccessLogEntry {
  timestamp: string;
  action: string;
  outing_type?: string;
  seat_number?: string;
}

interface WeekScheduleDay {
  date: string;
  day_label: string;
  ranges: { start: string; end: string }[];
  is_exempt: boolean;
  is_today: boolean;
}

interface WeekScheduleResult {
  week_start: string;
  week_end: string;
  today: string;
  upcoming_from: string | null;
  days: WeekScheduleDay[];
}

interface AcademySchedule {
  id: string;
  academy_name: string;
  days_of_week: number[];
  start_time: string;
  end_time: string;
  grace_minutes: number;
  is_today: boolean;
}

interface PeriodTemplateInfo {
  period_number: number;
  period_name: string;
  period_type: string;
  start_time: string;
  end_time: string;
}

interface TodayExceptionInfo {
  exception_type: string;  // 결석/지각/조퇴/외출
  expected_arrival_time?: string | null;
  expected_departure_time?: string | null;
  reason?: string | null;
  status: string;  // pending / parent_approved / approved
  school_override?: boolean;
  submitter_role?: string | null;
}

interface StudentDetailData {
  student_id: string;  // UUID 문자열
  name: string;
  phone: string;
  parent_phone?: string;
  grade?: string;
  weekly_schedule: ScheduleInfo[];
  period_templates: PeriodTemplateInfo[];
  period_schedules: Record<string, number[]>;
  today_access_logs: AccessLogEntry[];
  recent_attendance_summary?: {
    total_days: number;
    days: Array<{
      date: string;
      check_ins: number;
      check_outs: number;
      first_checkin?: string;
      last_checkout?: string;
    }>;
  };
  today_exception?: TodayExceptionInfo | null;
}

const NOTE_TYPE_LABELS: Record<string, string> = {
  sleep: '졸음',
  off_task: '집중 저하',
  leave: '자리 비움',
  return: '자리 복귀',
  other: '기타',
};

// 커스텀 순찰/벌점 항목 (API에서 로드, 최대 5개)
interface PatrolPenaltyItem {
  id: string;
  name: string;
  emoji: string;
  points: number;
  point_type: 'demerit' | 'merit';
  color: string;
  display_order: number;
  is_active: boolean;
  notify_parent?: boolean;
}

// 색상 → CSS 클래스 매핑
const COLOR_CLASS_MAP: Record<string, { bg: string; selected: string; ring: string }> = {
  red: { bg: 'bg-red-100 border-red-400 text-red-700 hover:bg-red-200', selected: 'ring-red-400', ring: 'ring-red-400' },
  green: { bg: 'bg-green-100 border-green-400 text-green-700 hover:bg-green-200', selected: 'ring-green-400', ring: 'ring-green-400' },
  gray: { bg: 'bg-gray-100 border-gray-400 text-gray-700 hover:bg-gray-200', selected: 'ring-gray-400', ring: 'ring-gray-400' },
  blue: { bg: 'bg-blue-100 border-blue-400 text-blue-700 hover:bg-blue-200', selected: 'ring-blue-400', ring: 'ring-blue-400' },
  orange: { bg: 'bg-orange-100 border-orange-400 text-orange-700 hover:bg-orange-200', selected: 'ring-orange-400', ring: 'ring-orange-400' },
};

// 🔴 순찰 항목은 [벌점 관리] → 설정의 매장 목록 하나만 쓴다 (2026-10-01 대표 지시).
//    종전엔 목록을 못 불러오면 고정 항목(졸음·핸드폰…)을 대신 띄워 매장 목록과 다른 기록이 남았다.

const ACTION_LABELS: Record<string, string> = {
  check_in: '입실',
  check_out: '퇴실',
  outing: '외출',
};

const OUTING_TYPE_LABELS: Record<string, string> = {
  meal: '🍚 식사',
  hospital: '🏥 병원',
  academy: '📚 학원',
  rest: '☕ 휴식',
  toilet: '🚻 화장실',
  other: '📝 기타',
};

const STATUS_LABELS: Record<string, string> = {
  occupied: '사용 중',
  available: '비어있음',
  reserved: '예약됨',
  cleaning: '청소 중',
  inactive: '비활성',
};

const STATUS_COLORS: Record<string, string> = {
  occupied: 'bg-green-100 text-green-800',
  available: 'bg-gray-100 text-gray-800',
  reserved: 'bg-yellow-100 text-yellow-800',
  cleaning: 'bg-red-100 text-red-800',
  inactive: 'bg-gray-200 text-gray-600',
};


// 빠른 연락 메모 프리셋
const CONTACT_PRESETS = [
  { label: '미등원예정', icon: '🚫' },
  { label: '지각예정', icon: '⏰' },
];

// 'YYYY-MM-DD' → '8/18' (서버가 이미 KST 기준 날짜 문자열을 준다. Date 로 파싱하면 UTC 로 밀린다)
const formatMD = (isoDate: string): string => {
  const [, m, d] = isoDate.split('-');
  return `${Number(m)}/${Number(d)}`;
};

const formatWeekRange = (week: { week_start: string; week_end: string } | null): string =>
  week ? `${formatMD(week.week_start)} ~ ${formatMD(week.week_end)}` : '';


export const SeatDetailPanel: React.FC<SeatDetailPanelProps> = ({
  seatDetail,
  onClose,
  onUnassignStudent,
  onRefresh,
}) => {
  const [selectedPatrolType, setSelectedPatrolType] = useState<string | null>(null);
  const [noteContent, setNoteContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [tardinessSubmitting, setTardinessSubmitting] = useState(false);

  // 커스텀 순찰/벌점 항목 (API 로드)
  const [patrolItems, setPatrolItems] = useState<PatrolPenaltyItem[]>([]);
  const [patrolItemsFailed, setPatrolItemsFailed] = useState(false);
  const [patrolItemsLoaded, setPatrolItemsLoaded] = useState(false);
  const [studentDetail, setStudentDetail] = useState<StudentDetailData | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [academySchedules, setAcademySchedules] = useState<AcademySchedule[]>([]);
  const [loadingAcademy, setLoadingAcademy] = useState(false);
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [weekSchedule, setWeekSchedule] = useState<WeekScheduleResult | null>(null);
  // 0 = 이번 주. 승인된 스케줄은 적용일부터라서 다음 주를 볼 수 있어야 한다.
  const [weekOffset, setWeekOffset] = useState(0);
  const [sendingAlert, setSendingAlert] = useState(false);
  const [alertResult, setAlertResult] = useState<{ success: boolean; message: string } | null>(null);
  // 🔴 미출석 알림은 누르는 즉시 학부모에게 알림톡이 나간다. 확인 한 단계를 둔다. (2026-09-13)
  //    이 패널은 스크롤되는 모달이고 버튼이 전체 폭이라, 목록을 훑다 손이 닿으면 그대로 발송됐다
  //    (마포대흥점 제보 — "스크롤 하다가 눌린다").
  //    ⚠️ window.confirm 은 매장 환경에 따라 차단돼 "눌러도 반응 없음" 이 되므로 쓰지 않는다.
  const [confirmAlert, setConfirmAlert] = useState(false);
  // 📮 사유서 확인 요청 (2026-10-27 마포대흥점 건의) — 학부모 승인 전 사유서를 학부모께 다시 알린다.
  //    하루 1번(서버가 막는다). 알림톡이 바로 나가므로 미출석 알림처럼 한 번 더 묻는다(창 안 버튼).
  const [reminderConfirm, setReminderConfirm] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderSent, setReminderSent] = useState<{ exId: string; at: string; by?: string | null } | null>(null);
  const [reminderError, setReminderError] = useState<string | null>(null);
  // 오늘 이 학생에게 보낸 미출석 알림 기록 — 버튼 옆에서 바로 확인 (2026-10-15 대표 지시)
  //    studentId 를 같이 들고 있어, 학생을 빠르게 바꿨을 때 앞 학생 기록이 보이지 않게 한다.
  const [alertHistory, setAlertHistory] = useState<{
    studentId: string;
    total: number;
    success_count: number;
    items: Array<{ time: string; status: string; error_message?: string | null; sent_by?: string | null; recipient?: string | null }>;
  } | null>(null);
  const [alertHistoryFailed, setAlertHistoryFailed] = useState(false);
  const [showAlertHistory, setShowAlertHistory] = useState(false);

  // 메모 state
  const [memos, setMemos] = useState<Array<{
    id: string; content: string; category: string;
    author_name: string; created_at: string; is_pinned: boolean;
    memo_date?: string | null;
  }>>([]);
  const [memosTotal, setMemosTotal] = useState(0);
  const [isLoadingMemos, setIsLoadingMemos] = useState(false);
  const [newMemoContent, setNewMemoContent] = useState('');
  const [memoDateInput, setMemoDateInput] = useState('');
  const [showAllMemos, setShowAllMemos] = useState(false);

  const MEMO_CATS: Record<string, string> = {
    general: '일반', academic: '학업', behavior: '행동',
    health: '건강', family: '가정', other: '기타',
  };

  const loadMemos = async (studentId: string, offset = 0) => {
    setIsLoadingMemos(true);
    try {
      const response = await apiClient.get(`/v1/owner/member-memos?student_id=${studentId}&limit=20&offset=${offset}`);
      if (response.data?.success) {
        if (offset === 0) {
          setMemos(response.data.data.memos);
        } else {
          setMemos((prev: typeof memos) => [...prev, ...response.data.data.memos]);
        }
        setMemosTotal(response.data.data.total);
      }
    } catch (error) {
      console.error('메모 로드 실패:', error);
    } finally {
      setIsLoadingMemos(false);
    }
  };

  const handleMemoSubmit = async (studentId: string) => {
    if (!newMemoContent.trim()) return;
    try {
      await apiClient.post('/v1/owner/member-memos', {
        student_id: studentId,
        content: newMemoContent.trim(),
        category: memoDateInput ? 'contact' : 'general',
        ...(memoDateInput ? { memo_date: memoDateInput } : {}),
      });
      setNewMemoContent('');
      loadMemos(studentId);
    } catch (error) {
      console.error('메모 작성 실패:', error);
    }
  };

  const handleMemoPin = async (memoId: string, studentId: string, currentPinned: boolean) => {
    try {
      await apiClient.patch(`/v1/owner/member-memos/${memoId}`, {
        is_pinned: !currentPinned,
      });
      loadMemos(studentId);
    } catch (error) {
      console.error('메모 고정 실패:', error);
    }
  };

  const handleMemoDelete = async (memoId: string, studentId: string) => {
    if (!window.confirm('이 메모를 삭제하시겠습니까?')) return;
    try {
      await apiClient.delete(`/v1/owner/member-memos/${memoId}`);
      loadMemos(studentId);
    } catch (error) {
      console.error('메모 삭제 실패:', error);
    }
  };

  useEffect(() => {
    console.log('🔍 [SeatDetailPanel] seatDetail 변경:', {
      hasSeatDetail: !!seatDetail,
      hasStudent: !!seatDetail?.student,
      studentId: seatDetail?.student?.id,
      studentName: seatDetail?.student?.name
    });

    // 좌석/학생이 바뀌면 확인창을 먼저 닫는다 — 앞 학생용으로 열린 창이 남아
    // 다른 학생에게 그대로 발송되지 않게. (A→B 전환은 아래 if 쪽을 타므로 여기서 처리)
    setConfirmAlert(false);
    setAlertResult(null);
    setAlertHistory(null);
    setAlertHistoryFailed(false);
    setShowAlertHistory(false);

    if (seatDetail?.student?.id) {
      loadAlertHistory(seatDetail.student.id);
      loadStudentDetail(seatDetail.student.id);
      loadAcademySchedules(seatDetail.student.id);
      setWeekOffset(0);
      loadMemos(seatDetail.student.id);
    } else {
      setStudentDetail(null);
      setAcademySchedules([]);
      setWeekSchedule(null);
      setMemos([]);
      setMemosTotal(0);
      setShowAllMemos(false);
      setMemoDateInput('');
    }
  }, [seatDetail?.student?.id]);

  useEffect(() => {
    if (seatDetail?.student?.id) {
      loadWeekSchedule(seatDetail.student.id, weekOffset);
    }
  }, [seatDetail?.student?.id, weekOffset]);

  const loadStudentDetail = async (studentId: string) => {
    setLoadingDetail(true);
    try {
      const response = await apiClient.get<StudentDetailData>(`/v1/seat-monitoring/students/${studentId}`);
      setStudentDetail(response.data);
    } catch (error) {
      console.error('❌ [loadStudentDetail] 실패:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const loadAcademySchedules = async (studentId: string) => {
    setLoadingAcademy(true);
    try {
      const response = await apiClient.get<{ success: boolean; data: AcademySchedule[] }>(
        `/v1/owner/academy-schedules/student/${studentId}`
      );
      if (response.data.success) {
        setAcademySchedules(response.data.data);
      }
    } catch (error) {
      console.error('학원 스케줄 로드 실패:', error);
    } finally {
      setLoadingAcademy(false);
    }
  };

  // 일주일 스케줄 (하루 시간띠와 같은 원본을 쓰는 API)
  const loadWeekSchedule = async (studentId: string, offset = 0) => {
    try {
      const anchor = new Date();
      anchor.setDate(anchor.getDate() + offset * 7);
      const anchorStr = `${anchor.getFullYear()}-${String(anchor.getMonth() + 1).padStart(2, '0')}-${String(anchor.getDate()).padStart(2, '0')}`;
      const res = await apiClient.get<WeekScheduleResult>(
        `/v1/owner/attendance-period-grid/time-band/student/week?student_id=${studentId}&anchor_date=${anchorStr}`
      );
      setWeekSchedule(res.data);
    } catch (error) {
      console.error('주간 스케줄 로드 실패:', error);
      setWeekSchedule(null);
    }
  };

  const loadAlertHistory = async (studentId: string) => {
    try {
      const res = await apiClient.get<{
        total: number; success_count: number;
        items: Array<{ time: string; status: string; error_message?: string | null; sent_by?: string | null; recipient?: string | null }>;
      }>(`/v1/seat-monitoring/students/${studentId}/absent-alert-history`);
      setAlertHistory({ studentId, ...res.data });
      setAlertHistoryFailed(false);
    } catch (error) {
      console.error('미출석 알림 기록 조회 실패:', error);
      setAlertHistory(null);
      setAlertHistoryFailed(true);
    }
  };

  // 미출석 알림 발송
  const sendAbsentAlert = async () => {
    if (!student?.id) return;

    setConfirmAlert(false);
    setSendingAlert(true);
    setAlertResult(null);

    try {
      const response = await apiClient.post<{ success: boolean; message: string; sent_to?: string }>(
        `/v1/seat-monitoring/students/${student.id}/send-absent-alert`,
        { student_id: student.id }
      );

      setAlertResult({
        success: response.data.success,
        message: response.data.message
      });

      // 3초 후 결과 메시지 숨기기
      setTimeout(() => setAlertResult(null), 3000);
    } catch (error) {
      console.error('미출석 알림 발송 실패:', error);
      setAlertResult({
        success: false,
        message: '알림 발송에 실패했습니다.'
      });
    } finally {
      setSendingAlert(false);
      // 결과 메시지는 3초 뒤 사라지므로, 남는 기록을 바로 다시 불러온다
      loadAlertHistory(student.id);
    }
  };

  // 다른 학생 상세로 바뀌면 묻던 상태를 지운다(보낸 기록은 사유서 ID 로 구분되어 그대로 둬도 안전)
  useEffect(() => {
    setReminderConfirm(false);
    setReminderError(null);
  }, [seatDetail?.student?.id]);

  const sendParentReminder = async (exId: string) => {
    setReminderConfirm(false);
    setSendingReminder(true);
    setReminderError(null);
    try {
      const res = await apiClient.post<{ reminded_at: string; reminded_by?: string | null }>(
        `/v1/owner/exception-requests/${exId}/parent-reminder`
      );
      setReminderSent({ exId, at: res.data.reminded_at, by: res.data.reminded_by });
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : '보내지 못했습니다. 잠시 뒤 다시 시도해 주세요.';
      // 이미 보낸 경우(409) — 다른 PC 에서 방금 보냈을 수 있다. 시각을 그대로 보여 주고 막는다.
      const m = error?.response?.status === 409 ? /(\d{2}:\d{2})/.exec(msg) : null;
      if (m) setReminderSent({ exId, at: m[1] });
      else setReminderError(msg);
    } finally {
      setSendingReminder(false);
    }
  };

  const DAY_NAMES = ['', '월', '화', '수', '목', '금', '토', '일'];

  // 커스텀 순찰/벌점 항목 API 로드 (Hook은 조건부 return 전에 위치해야 함)
  useEffect(() => {
    if (!patrolItemsLoaded) {
      apiClient.get('/v1/owner/patrol-penalty-items')
        .then(res => {
          setPatrolItems(Array.isArray(res.data) ? res.data : []);
          setPatrolItemsFailed(false);
          setPatrolItemsLoaded(true);
        })
        .catch(() => {
          setPatrolItemsFailed(true);
          setPatrolItemsLoaded(true);
        });
    }
  }, [patrolItemsLoaded]);

  if (!seatDetail) {
    return null;
  }

  const { seat, student, current_session, recent_notes } = seatDetail;

  // 확인창에 띄울 학부모 번호가 '지금 이 학생' 것인지. 학생이 바뀌는 순간이나
  // 상세 조회가 실패한 뒤에는 앞 학생 값이 남아 있어 그대로 쓰면 안 된다.
  const detailMatchesStudent =
    !!studentDetail && !!student?.id && studentDetail.student_id === student.id;

  const handleCreateNote = async () => {
    if (!selectedPatrolType) {
      alert('유형을 선택해주세요.');
      return;
    }

    const selectedItem = patrolItems.find(item => item.id === selectedPatrolType);
    if (!selectedItem || !student) return;

    setSubmitting(true);
    try {
      // 매장 순찰 항목 하나로 — 출석부·QR 점검표가 같은 서버 함수(issue_patrol_item)를 탄다
      await apiClient.post('/v1/owner/patrol-penalty-items/issue', {
        item_id: selectedItem.id,
        student_id: student.id,
        seat_id: seat.seat_id,
        memo: noteContent.trim(),
      });

      setNoteContent('');
      setSelectedPatrolType(null);
      // 노트 저장 후 상세 정보 새로고침 (recent_notes 갱신)
      if (onRefresh) {
        await onRefresh(seat.seat_id);
      }
    } catch (error) {
      console.error('순찰/벌점 기록 실패:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // 지각 벌점 수동 부여 (시스템 항목, 우측 별도 버튼)
  const handleIssueTardiness = async () => {
    if (!student) return;
    if (!window.confirm(`${student.name} 학생에게 지각 벌점 1점을 부여하시겠습니까?`)) return;
    setTardinessSubmitting(true);
    try {
      await apiClient.post('/v1/owner/patrol-penalty-items/issue-tardiness', {
        student_id: student.id,
        seat_id: seat.seat_id,
        memo: noteContent.trim() || '지각',
        points: 1,
      });
      setNoteContent('');
      if (onRefresh) await onRefresh(seat.seat_id);
      alert('지각 벌점 1점이 부여되었습니다.');
    } catch (e: any) {
      alert(`지각 벌점 부여 실패: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setTardinessSubmitting(false);
    }
  };

  const handleUnassign = async () => {
    if (!student) {
      return;
    }

    const confirmed = window.confirm(
      `${student.name} 학생을 ${seat.seat_code}번 좌석에서 배정 해제하시겠습니까?`
    );

    if (confirmed) {
      try {
        await onUnassignStudent(seat.seat_id);
      } catch (error) {
        console.error('배정 해제 실패:', error);
      }
    }
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}시간 ${mins}분`;
  };

  const formatDateTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleString('ko-KR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 배경 오버레이 */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* 모달 컨텐츠 */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[922px] max-h-[90vh] overflow-y-auto mx-4">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-xl font-bold text-gray-900">
            {seat.seat_code}번 좌석
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

      <div className="p-6 space-y-6">
        {student && (
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">현재 학생</h3>

            {/* 출석 면제 표시 */}
            {seat.schedule_status === 'exempt' && (
              <div className="mb-3 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg">
                <span className="text-sm font-semibold text-purple-700">출석 면제</span>
                {seat.student_id && (
                  <span className="text-sm text-purple-600 ml-2">
                    (사유서 승인)
                  </span>
                )}
              </div>
            )}

            {/* ⭐ B작업 Step 1 — 오늘 종합 (교시별 출결·사유서·벌점·순찰 통합)
                seat.student_id는 옵션이라 백엔드가 안 채울 수 있어 student.id 사용 */}
            {student?.id && <StudentTodaySummary studentId={student.id} />}
            {/* 학생별 순찰 기록 (2026-10-02) — 학부모 앱·순찰 태블릿과 같은 기록 */}
            {student?.id && <PatrolChecksStrip studentId={student.id} />}

            <div className="flex items-center gap-4 mb-4">
              {student.photo_url ? (
                <img src={student.photo_url} alt={student.name} className="w-16 h-16 rounded-full object-cover" />
              ) : (
                <UserCircleIcon className="w-16 h-16 text-gray-400" />
              )}
              <div>
                <p className="text-base font-semibold text-gray-900">{student.name}</p>
                <p className="text-sm text-gray-600">📱 {student.phone || '전화번호 없음'}</p>
                {studentDetail?.parent_phone && (
                  <p className="text-sm text-gray-600">👨‍👩‍👧 학부모: {studentDetail.parent_phone}</p>
                )}
              </div>
            </div>

            {current_session && (
              <div className="space-y-2 border-t border-blue-200 pt-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">순공부시간</span>
                  <span className="text-sm font-bold text-blue-600">
                    {formatDuration(current_session.pure_study_minutes)}
                  </span>
                </div>
              </div>
            )}

            {/* 미출석/지각 알림 버튼 */}
            <div className="border-t border-blue-200 pt-3 mt-3">
              {/* 🔴 승인 대기 사유서가 있으면 **버튼 위에** 먼저 보여준다.
                  2026-09-19 송파점: 학생이 09:36 에 사유서를 냈는데 표시가 없어
                  원장이 19초 뒤 미출석 알림을 보냈다(승인은 11:26). 알 방법이 없었다.
                  막지는 않는다 — 보낼지 말지는 원장이 정한다. */}
              {seatDetail?.seat?.pending_exception && (
                <div className="mb-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-300 text-amber-900 text-sm">
                  <b>⏳ {seatDetail.seat.pending_exception.type} 사유서가 제출됐습니다</b>
                  <span className="ml-1 text-amber-700">(아직 승인 전)</span>
                  {seatDetail.seat.pending_exception.time && (
                    <div className="mt-0.5 text-xs text-amber-800">
                      {seatDetail.seat.pending_exception.time} 예정
                    </div>
                  )}
                  {seatDetail.seat.pending_exception.reason && (
                    <div className="mt-0.5 text-xs text-amber-800">
                      사유: {seatDetail.seat.pending_exception.reason}
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-stretch gap-2">
                <button
                  onClick={() => {
                    setAlertResult(null);
                    setConfirmAlert(true);
                    // 다른 PC·다른 직원이 방금 보냈을 수 있으니 확인 창을 열 때 다시 읽는다
                    if (student?.id) loadAlertHistory(student.id);
                  }}
                  disabled={sendingAlert}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <BellAlertIcon className="w-5 h-5" />
                  {sendingAlert ? '발송 중...' : '📱 학부모에게 미출석 알림 보내기'}
                </button>
                {/* 오늘 보낸 기록 — 보냈는지·몇 번·몇 시에 그 자리에서 확인 (2026-10-15) */}
                {(() => {
                  const h = alertHistory && alertHistory.studentId === student?.id ? alertHistory : null;
                  if (!h) {
                    return (
                      <div className="shrink-0 w-24 flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-[11px] text-gray-500 leading-tight text-center px-1">
                        {alertHistoryFailed ? '기록을 못 불러옴' : '기록 확인 중…'}
                      </div>
                    );
                  }
                  if (h.total === 0) {
                    return (
                      <div className="shrink-0 w-24 flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-xs text-gray-600 leading-tight text-center px-1">
                        <span className="font-semibold">오늘</span>
                        <span>보낸 적 없음</span>
                      </div>
                    );
                  }
                  const last = h.items[0];
                  const lastOk = last.status === 'success';
                  // 'skipped' = 보내지 않음(연타 중복 · 알림톡 차단 등). 실패(빨강)와 구분한다.
                  const lastSkipped = last.status === 'skipped';
                  return (
                    <button
                      type="button"
                      onClick={() => setShowAlertHistory(v => !v)}
                      title="눌러서 오늘 보낸 기록 전체 보기"
                      className={`shrink-0 w-24 flex flex-col items-center justify-center rounded-lg border text-xs leading-tight text-center px-1 ${
                        lastOk ? 'border-green-300 bg-green-50 text-green-800'
                          : lastSkipped ? 'border-gray-300 bg-gray-50 text-gray-700'
                          : 'border-red-300 bg-red-50 text-red-700'
                      }`}
                    >
                      <span className="font-semibold">오늘 {h.success_count}회 발송</span>
                      <span>
                        {lastOk ? '마지막' : lastSkipped ? '마지막 안 보냄' : '마지막 실패'} {last.time}
                      </span>
                      <span className="text-[10px] text-gray-500">{showAlertHistory ? '▲ 접기' : '▼ 기록'}</span>
                    </button>
                  );
                })()}
              </div>
              {/* 📮 학부모 승인 전 사유서 → 학부모께 확인 요청(사유서 접수 알림톡 다시 보내기, 하루 1번) */}
              {(() => {
                const pe = seatDetail?.seat?.pending_exception;
                if (!pe || pe.status !== 'pending' || !pe.id) return null;
                const sent = reminderSent && reminderSent.exId === pe.id
                  ? reminderSent
                  : pe.reminded_at ? { exId: pe.id, at: pe.reminded_at, by: pe.reminded_by } : null;
                return (
                  <div className="mt-2">
                    {sent ? (
                      <div className="w-full px-4 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-sm text-center">
                        📮 사유서 확인 요청 — <b>오늘 {sent.at} 보냄</b>
                        {sent.by ? <span className="text-amber-700"> · {sent.by}</span> : null}
                        <div className="text-xs text-amber-700">같은 사유서는 하루 한 번만 보낼 수 있습니다</div>
                      </div>
                    ) : reminderConfirm ? (
                      <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                        학부모님께 <b>“{pe.type} 사유서가 제출됐으니 확인해 주세요”</b> 알림톡을 보냅니다.
                        <div className="text-xs text-amber-700 mt-0.5">사유서 접수 때 나간 것과 같은 알림톡 · 하루 한 번</div>
                        <div className="mt-2 flex gap-2">
                          <button
                            type="button"
                            onClick={() => setReminderConfirm(false)}
                            className="flex-1 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                          >
                            취소
                          </button>
                          <button
                            type="button"
                            onClick={() => sendParentReminder(pe.id as string)}
                            className="flex-1 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-semibold"
                          >
                            보내기
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => { setReminderError(null); setReminderConfirm(true); }}
                        disabled={sendingReminder}
                        className="w-full px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:bg-gray-400 text-white text-sm font-medium transition-colors"
                      >
                        {sendingReminder ? '보내는 중...' : '📮 사유서 확인 요청 (학부모 승인 전)'}
                      </button>
                    )}
                    {reminderError && <p className="mt-1 text-sm text-center text-red-600">{reminderError}</p>}
                  </div>
                );
              })()}
              {showAlertHistory && alertHistory && alertHistory.studentId === student?.id && alertHistory.items.length > 0 && (
                <ul className="mt-2 rounded-lg border border-gray-200 bg-white divide-y divide-gray-100 text-xs">
                  {alertHistory.items.map((it, i) => (
                    <li key={i} className="px-3 py-1.5 flex items-start gap-2">
                      <span className="font-mono text-gray-700 shrink-0">{it.time}</span>
                      {it.status === 'success' ? (
                        <span className="text-green-700 shrink-0">✓ 발송됨</span>
                      ) : it.status === 'skipped' ? (
                        <span className="text-gray-500 shrink-0">− 안 보냄</span>
                      ) : (
                        <span className="text-red-600 shrink-0">✗ 실패</span>
                      )}
                      <span className="text-gray-500 min-w-0 break-all">
                        {[it.sent_by ? `${it.sent_by}` : null, it.recipient, it.status !== 'success' && it.error_message ? it.error_message : null]
                          .filter(Boolean).join(' · ')}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {alertResult && (
                <p className={`mt-2 text-sm text-center ${alertResult.success ? 'text-green-600' : 'text-red-600'}`}>
                  {alertResult.message}
                </p>
              )}
            </div>

            {/* 보내기 전 확인 — 누구에게 나가는지 보여주고 한 번 더 묻는다.
                이 패널이 z-50 이므로 그보다 위로 띄운다. */}
            {confirmAlert && (
              <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
                <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5">
                  <div className="text-base font-bold text-gray-900">
                    미출석 알림을 보낼까요?
                  </div>
                  {seatDetail?.seat?.pending_exception && (
                    <div className="mt-3 px-3 py-2 rounded bg-amber-50 border border-amber-300 text-amber-900 text-sm">
                      ⏳ 이 학생은 <b>{seatDetail.seat.pending_exception.type} 사유서</b>를
                      제출했고 아직 승인 전입니다.
                      {seatDetail.seat.pending_exception.time
                        ? ` (${seatDetail.seat.pending_exception.time} 예정)`
                        : ''}
                    </div>
                  )}
                  {/* 🔴 보낼지 정하는 바로 이 순간에 '오늘 이미 보냈다'를 보여준다 (2026-10-15) */}
                  {(() => {
                    const h = alertHistory && alertHistory.studentId === student?.id ? alertHistory : null;
                    if (!h || h.success_count === 0) return null;
                    const lastSent = h.items.find(it => it.status === 'success');
                    return (
                      <div className="mt-3 px-3 py-2 rounded bg-blue-50 border border-blue-300 text-blue-900 text-sm">
                        오늘 이미 <b>{h.success_count}회</b> 보냈습니다
                        {lastSent ? <> (마지막 <b>{lastSent.time}</b>{lastSent.sent_by ? `, ${lastSent.sent_by}` : ''})</> : null}.
                      </div>
                    );
                  })()}
                  <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-800">
                    <div>
                      <span className="text-gray-500">학생</span>{' '}
                      <b>{student?.name || '이름없음'}</b>
                    </div>
                    <div className="mt-1">
                      <span className="text-gray-500">받는 분</span>{' '}
                      {/* 🔴 이름은 좌석 정보에서, 번호는 따로 부르는 학생 상세에서 온다.
                          두 출처가 어긋날 수 있으므로 같은 학생 것일 때만 번호를 쓴다.
                          어긋난 채 보여주면 "A 학생 / B 학부모 번호" 가 되어 확인창이 거짓이 된다. */}
                      {detailMatchesStudent ? (
                        studentDetail?.parent_phone ? (
                          <b>학부모 {studentDetail.parent_phone}</b>
                        ) : (
                          <span className="text-red-600 font-medium">
                            학부모 번호가 등록되어 있지 않습니다
                          </span>
                        )
                      ) : loadingDetail ? (
                        <span className="text-gray-500">학부모 번호 확인 중…</span>
                      ) : (
                        /* 조회 실패 — '번호가 없다'고 단정하면 안 된다. 있는데 못 불러왔을 수 있다. */
                        <span className="text-amber-700">
                          번호를 불러오지 못했습니다 (이 학생의 학부모에게 발송됩니다)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-gray-600">
                    보내면 학부모님께 <b>카카오 알림톡이 바로 나가고, 되돌릴 수 없습니다.</b>
                    {' '}건당 요금도 발생합니다.
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      onClick={() => setConfirmAlert(false)}
                      className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
                    >
                      취소
                    </button>
                    <button
                      onClick={sendAbsentAlert}
                      disabled={sendingAlert}
                      className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
                    >
                      보내기
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        )}

        {studentDetail && !loadingDetail && (
          <>
            {/* 오늘의 입출입 기록 — 높이 고정 + 스크롤 (아래 내용이 밀리지 않게) */}
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <ArrowRightOnRectangleIcon className="w-5 h-5 text-green-600" />
                <h3 className="text-sm font-semibold text-gray-700">오늘의 입출입 기록</h3>
                {studentDetail.today_access_logs.length > 0 && (
                  <span className="text-xs text-gray-500">
                    ({studentDetail.today_access_logs.length}건)
                  </span>
                )}
              </div>
              {studentDetail.today_access_logs.length > 0 ? (
                <div className="space-y-2 h-48 overflow-y-auto pr-1">
                  {studentDetail.today_access_logs.map((log, idx) => {
                    const getActionStyle = (action: string) => {
                      switch (action) {
                        case 'check_in':
                          return { color: 'text-green-700', bg: 'bg-green-50', icon: '✅' };
                        case 'check_out':
                          return { color: 'text-gray-700', bg: 'bg-gray-100', icon: '🚪' };
                        case 'outing':
                          return { color: 'text-blue-700', bg: 'bg-blue-50', icon: '🚶' };
                        default:
                          return { color: 'text-gray-700', bg: 'bg-gray-50', icon: '' };
                      }
                    };
                    const style = getActionStyle(log.action);

                    return (
                      <div key={idx} className={`flex justify-between items-start p-2 ${style.bg} rounded`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${style.color}`}>
                              {style.icon} {ACTION_LABELS[log.action] || log.action}
                            </span>
                            {log.outing_type && (
                              <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded font-medium">
                                {OUTING_TYPE_LABELS[log.outing_type] || log.outing_type}
                              </span>
                            )}
                          </div>
                          {log.seat_number && (
                            <p className="text-xs text-gray-500 mt-1">좌석: {log.seat_number}</p>
                          )}
                        </div>
                        <span className="text-xs text-gray-600 font-medium">
                          {new Date(log.timestamp).toLocaleTimeString('ko-KR', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center h-48 flex items-center justify-center">
                  오늘 입출입 기록이 없습니다
                </p>
              )}
            </div>

            <div className="bg-white rounded-lg p-4 border border-gray-200">
              {(() => {
                const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];
                // 논리적 날짜 경계: 새벽 2시 전이면 전날로 판단
                const now = new Date();
                const logicalDate = now.getHours() < 2
                  ? new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
                  : now;
                const todayIdx = logicalDate.getDay();
                const todayLabel = DAY_LABELS[todayIdx];
                // 백엔드는 Python weekday 규칙(0=월, 6=일)으로 day_of_week 반환
                // JS getDay()(0=일, 6=토)을 Python 규칙으로 변환해서 조회
                const jsToPyDow = (js: number) => (js + 6) % 7;
                const todayPyDow = jsToPyDow(todayIdx);
                const todayWeekly = studentDetail.weekly_schedule.find(s => s.day_of_week === todayPyDow);
                const todayAcademySchedules = academySchedules.filter(s => s.is_today);
                const todayWeekRow = weekSchedule?.days?.find(d => d.is_today) || null;
                const todayRanges = todayWeekRow?.ranges || [];

                return (
                  <>
                    {/* 헤더 */}
                    <div className="flex items-center gap-2 mb-3">
                      <CalendarIcon className="w-5 h-5 text-blue-600" />
                      <h3 className="text-sm font-semibold text-gray-700">
                        {showFullSchedule ? '일주일 스케줄' : `오늘 스케줄 (${todayLabel})`}
                      </h3>
                    </div>

                    {!showFullSchedule ? (
                      /* === 당일 요약 뷰 === */
                      <div>
                        {/* 오늘 하루 시간띠 (시간 단일진실 + 교시 구분선 + 교시별출결) */}
                        {student?.id && (
                          <div className="mb-3 pb-3 border-b border-gray-100">
                            <StudentTimeBand studentId={student.id} />
                          </div>
                        )}
                        {/* 오늘 낸 스케줄 = 시간 그대로 (교시는 표시용이라 시간이 진실) */}
                        {todayRanges.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {todayRanges.map((r, i) => (
                              <div
                                key={`${r.start}-${i}`}
                                className="px-2.5 py-1.5 rounded-lg border bg-blue-50 border-blue-300 text-blue-800 text-sm font-medium"
                              >
                                {r.start} ~ {r.end}
                              </div>
                            ))}
                          </div>
                        ) : todayWeekly ? (
                          <div className="flex items-center gap-3 p-2 bg-blue-50 rounded-lg">
                            <span className="text-sm font-medium text-blue-900">{todayLabel}요일</span>
                            <span className="text-sm text-blue-700">
                              입실 {todayWeekly.expected_check_in} ~ 퇴실 {todayWeekly.expected_check_out}
                            </span>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">오늘은 등록된 스케줄이 없습니다</p>
                        )}

                        {/* 오늘 학원 스케줄 */}
                        {todayAcademySchedules.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {todayAcademySchedules.map((schedule) => (
                              <div key={schedule.id} className="flex items-center gap-2 px-2 py-1 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-700">
                                <span className="font-medium">{schedule.academy_name}</span>
                                <span>{schedule.start_time}→{schedule.end_time}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      /* === 일주일 보기 (월~일, 실제 낸 시간) === */
                      <div>
                        {/* 주 이동 — 승인된 스케줄은 '적용일'부터라 다음 주를 봐야 보이는 경우가 있다 */}
                        <div className="flex items-center justify-between mb-2">
                          <button
                            onClick={() => setWeekOffset(weekOffset - 1)}
                            className="px-2 py-1 text-xs text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded"
                          >
                            ◀ 지난주
                          </button>
                          <span className="text-xs font-medium text-gray-600">
                            {weekOffset === 0 ? '이번 주' : formatWeekRange(weekSchedule)}
                          </span>
                          <button
                            onClick={() => setWeekOffset(weekOffset + 1)}
                            className="px-2 py-1 text-xs text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded"
                          >
                            다음주 ▶
                          </button>
                        </div>

                        {/* 승인은 됐지만 아직 시작 안 한 스케줄 안내 */}
                        {weekSchedule?.upcoming_from && (
                          <div className="mb-2 px-2 py-1.5 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800">
                            🕒 {formatMD(weekSchedule.upcoming_from)}부터 적용되는 새 스케줄이 있습니다.
                            그 전까지는 이전 스케줄이 그대로 표시됩니다.
                          </div>
                        )}

                        {weekSchedule && weekSchedule.days.length > 0 ? (
                          <div className="space-y-1">
                            {weekSchedule.days.map((d) => (
                              <div
                                key={d.date}
                                className={`flex items-start gap-3 px-2 py-1.5 rounded ${
                                  d.is_today ? 'bg-blue-100 border border-blue-300' : 'bg-gray-50'
                                }`}
                              >
                                <span className={`text-sm font-semibold shrink-0 ${
                                  d.ranges.length > 0 ? 'text-blue-900' : 'text-gray-400'
                                }`}>
                                  {d.day_label} <span className="font-normal text-gray-400">{formatMD(d.date)}</span>
                                </span>
                                {d.ranges.length > 0 ? (
                                  <span className="text-sm text-blue-800">
                                    {d.ranges.map((r) => `${r.start} ~ ${r.end}`).join(', ')}
                                  </span>
                                ) : (
                                  <span className="text-sm text-gray-400">없음</span>
                                )}
                                {d.is_exempt && (
                                  <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 shrink-0">
                                    사유서
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 text-center py-2">등록된 스케줄이 없습니다</p>
                        )}

                        {/* 전체 학원 스케줄 */}
                        {academySchedules.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="flex items-center gap-1 mb-2">
                              <AcademicCapIcon className="w-4 h-4 text-orange-600" />
                              <span className="text-xs font-semibold text-gray-600">학원 스케줄</span>
                            </div>
                            <div className="space-y-2">
                              {academySchedules.map((schedule) => (
                                <div
                                  key={schedule.id}
                                  className={`p-2 rounded-lg border text-xs ${
                                    schedule.is_today
                                      ? 'bg-orange-50 border-orange-300'
                                      : 'bg-gray-50 border-gray-200'
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className={`font-semibold ${schedule.is_today ? 'text-orange-800' : 'text-gray-700'}`}>
                                      {schedule.academy_name}
                                    </span>
                                    {schedule.is_today && (
                                      <span className="text-xs bg-orange-500 text-white px-1.5 py-0.5 rounded-full">오늘</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 mt-1">
                                    <span className="text-gray-600">
                                      {schedule.days_of_week.map(d => DAY_LABELS[d]).join(', ')}
                                    </span>
                                    <span className={schedule.is_today ? 'text-orange-700 font-medium' : 'text-gray-600'}>
                                      {schedule.start_time} → {schedule.end_time}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 토글 버튼 */}
                    <button
                      onClick={() => setShowFullSchedule(!showFullSchedule)}
                      className="w-full mt-3 py-1.5 flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      {showFullSchedule ? (
                        <>
                          <ChevronUpIcon className="w-4 h-4" />
                          접기
                        </>
                      ) : (
                        <>
                          <ChevronDownIcon className="w-4 h-4" />
                          일주일 스케줄 자세히 보기
                        </>
                      )}
                    </button>
                  </>
                );
              })()}
            </div>

            {/* 메모 섹션 (날짜 메모 통합) */}
            {student && (
              <div className="bg-white rounded-lg p-4 border border-purple-200">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">📝</span>
                  <h3 className="text-sm font-semibold text-gray-700">메모</h3>
                  {memosTotal > 0 && (
                    <span className="text-xs text-gray-500">({memosTotal}건)</span>
                  )}
                </div>

                {/* 빠른 연락 메모 버튼 (클릭하면 입력란에 채움) */}
                <div className="flex gap-2 mb-2">
                  {CONTACT_PRESETS.map((preset) => (
                    <button
                      key={preset.label}
                      onClick={() => {
                        setNewMemoContent(prev => prev ? `${preset.label} - ${prev}` : `${preset.label} - `);
                        if (!memoDateInput) {
                          setMemoDateInput(getTodayKST());
                        }
                      }}
                      className="px-3 py-1.5 bg-orange-50 hover:bg-orange-100 border border-orange-300 rounded-lg text-xs font-medium text-orange-700 transition-colors"
                    >
                      {preset.icon} {preset.label}
                    </button>
                  ))}
                  <input
                    type="date"
                    value={memoDateInput}
                    onChange={(e) => setMemoDateInput(e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded-lg text-xs ml-auto"
                    title="메모 날짜 (비우면 일반 메모)"
                  />
                </div>

                {/* 메모 작성 */}
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newMemoContent}
                    onChange={(e) => setNewMemoContent(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newMemoContent.trim()) {
                        handleMemoSubmit(student.id);
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                    placeholder="메모를 입력하세요..."
                  />
                  <button
                    onClick={() => handleMemoSubmit(student.id)}
                    disabled={!newMemoContent.trim()}
                    className="px-3 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    저장
                  </button>
                </div>

                {/* 메모 목록 */}
                {isLoadingMemos ? (
                  <p className="text-sm text-gray-500 text-center py-2">로딩 중...</p>
                ) : memos.length > 0 ? (
                  <div className="space-y-2">
                    {(showAllMemos ? memos : memos.slice(0, 2)).map((memo) => (
                      <div key={memo.id} className={`p-2 rounded-lg border ${
                        memo.memo_date ? 'bg-orange-50 border-orange-200' :
                        memo.is_pinned ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {memo.is_pinned && (
                              <span className="text-xs font-medium text-yellow-700 bg-yellow-200 px-1 rounded mr-1">📌고정</span>
                            )}
                            {memo.memo_date && (
                              <span className="text-xs font-medium text-orange-600 mr-1">📅{memo.memo_date}</span>
                            )}
                            <span className="text-sm text-gray-800">{memo.content}</span>
                          </div>
                          <div className="flex items-center gap-1 ml-2 shrink-0">
                            <button
                              onClick={() => handleMemoPin(memo.id, student.id, memo.is_pinned)}
                              className={`text-xs px-1 ${memo.is_pinned ? 'text-yellow-600' : 'text-gray-400 hover:text-yellow-500'}`}
                              title={memo.is_pinned ? '고정 해제' : '고정'}
                            >
                              📌
                            </button>
                            <button
                              onClick={() => handleMemoDelete(memo.id, student.id)}
                              className="text-xs text-red-400 hover:text-red-600"
                            >
                              삭제
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-400">{memo.author_name}</span>
                          <span className="text-xs text-gray-400">
                            {new Date(memo.created_at).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                    ))}
                    {!showAllMemos && memos.length > 2 && (
                      <button
                        onClick={() => setShowAllMemos(true)}
                        className="w-full text-center text-xs text-purple-600 hover:text-purple-800 py-1"
                      >
                        + {memosTotal - 2}건 더보기
                      </button>
                    )}
                    {showAllMemos && memos.length < memosTotal && (
                      <button
                        onClick={() => loadMemos(student.id, memos.length)}
                        className="w-full text-center text-xs text-purple-600 hover:text-purple-800 py-1"
                      >
                        + 더 불러오기 ({memosTotal - memos.length}건 남음)
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-2">등록된 메모가 없습니다</p>
                )}
              </div>
            )}

            {/* 순찰/벌점 기록 — 커스텀 항목 (API 기반, 최대 5개) */}
            {student && (
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">⭐ 순찰/벌점 기록</h3>
                  {patrolItems.some(i => i.notify_parent) && (
                    <span className="text-[10px] text-gray-500 flex items-center gap-1">
                      <span className="bg-blue-500 text-white text-[9px] font-bold px-1 py-0.5 rounded-full leading-none">📨</span>
                      = 부여 시 학부모 알림톡
                    </span>
                  )}
                </div>

                {patrolItemsFailed && (
                  <div className="text-xs text-red-600 mb-2">
                    순찰 항목을 불러오지 못했습니다.{' '}
                    <button className="underline" onClick={() => setPatrolItemsLoaded(false)}>다시 불러오기</button>
                  </div>
                )}
                {/* 커스텀 항목 버튼들 + 지각 버튼 (우측 분리) */}
                <div className="flex justify-center items-center gap-2 mb-3">
                  {patrolItems.filter(i => i.is_active).map((item) => {
                    const colorCfg = COLOR_CLASS_MAP[item.color] || COLOR_CLASS_MAP['gray'];
                    const isSelected = selectedPatrolType === item.id;
                    const pointLabel = item.points === 0 ? '' : item.point_type === 'merit' ? `+${item.points}` : `-${item.points}`;
                    return (
                      <button
                        key={item.id}
                        onClick={() => setSelectedPatrolType(isSelected ? null : item.id)}
                        title={item.notify_parent ? '부여 시 학부모에게 알림톡 발송' : ''}
                        className={`relative flex flex-col items-center p-2 rounded-lg border-2 transition-all min-w-[56px] ${
                          isSelected
                            ? `${colorCfg.bg} ring-2 ring-offset-1 ${colorCfg.ring}`
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {item.notify_parent && (
                          <span
                            className="absolute -top-1.5 -right-1.5 bg-blue-500 text-white text-[9px] font-bold px-1 py-0.5 rounded-full leading-none shadow"
                            title="학부모 알림톡 발송"
                          >
                            📨
                          </span>
                        )}
                        <span className="text-2xl">{item.emoji}</span>
                        <span className="text-xs mt-1 font-medium">{item.name}</span>
                        {pointLabel && (
                          <span className={`text-xs ${item.point_type === 'merit' ? 'text-green-600' : 'text-red-600'}`}>
                            {pointLabel}점
                          </span>
                        )}
                      </button>
                    );
                  })}
                  {/* 우측 분리선 */}
                  <div className="w-px h-12 bg-gray-300 mx-1" />
                  {/* 지각 버튼 (수동, 즉시 부여) */}
                  <button
                    onClick={handleIssueTardiness}
                    disabled={tardinessSubmitting}
                    title="클릭 즉시 지각 벌점 1점 부여"
                    className="flex flex-col items-center p-2 rounded-lg border-2 transition-all min-w-[56px] bg-orange-50 border-orange-300 hover:bg-orange-100 disabled:opacity-50"
                  >
                    <span className="text-2xl">⏰</span>
                    <span className="text-xs mt-1 font-medium text-orange-700">지각</span>
                    <span className="text-xs text-red-600">-1점</span>
                  </button>
                </div>

                {/* 메모 입력 (선택사항) */}
                <div className="mb-3">
                  <input
                    type="text"
                    value={noteContent}
                    onChange={(e) => setNoteContent(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="추가 메모 (선택사항)"
                  />
                </div>

                {/* 저장 버튼 */}
                <button
                  onClick={handleCreateNote}
                  disabled={submitting || !selectedPatrolType}
                  className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedPatrolType
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {submitting ? '저장 중...' : selectedPatrolType ? '✓ 기록 저장 (순찰 + 벌점)' : '유형을 선택하세요'}
                </button>
              </div>
            )}

            {studentDetail.recent_attendance_summary && studentDetail.recent_attendance_summary.days.length > 0 && (
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <ClockIcon className="w-5 h-5 text-purple-600" />
                  <h3 className="text-sm font-semibold text-gray-700">최근 7일 출석</h3>
                </div>
                <div className="space-y-2">
                  {studentDetail.recent_attendance_summary.days.map((day) => (
                    <div key={day.date} className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
                      <span className="text-gray-700 font-medium">
                        {new Date(day.date).toLocaleDateString('ko-KR', {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                      <div className="flex gap-3 text-xs">
                        <span className="text-green-600">입실 {day.check_ins}회</span>
                        <span className="text-orange-600">외출 {day.check_outs}회</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {loadingDetail && (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">학생 정보 로딩 중...</p>
          </div>
        )}


        {recent_notes && recent_notes.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">최근 순찰 노트</h3>
            <div className="space-y-2">
              {recent_notes.map((note) => (
                <div key={note.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-blue-600">
                      {NOTE_TYPE_LABELS[note.note_type]}
                    </span>
                    <span className="text-xs text-gray-500">{formatDateTime(note.created_at)}</span>
                  </div>
                  <p className="text-sm text-gray-700">{note.note_content}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};
